import React, { useState, useEffect, Suspense, useRef } from 'react'
import { useAppSelector, useAppDispatch, usePage, useAdminPermissions } from 'app/hooks'
import { RootState, ErrorMessage, PageType, UserRole } from 'app/types'
import {
    Media,
    OptionBar,
    BreadCrumbs,
    ScrollHelper,
    Spinner,
    Button,
    IconButton,
    Message,
} from 'components'
import { organizationContent, projectContent } from 'app/transformers'
import { Footer } from 'views/HomePage/Footer'
import { getWindowLocation } from 'helpers/config'
import { navigateAsync, navigate } from 'actions/appActions'
import { PromptType } from 'app/types'
import { logger } from 'helpers/logger'

interface CustomPageProps {
    app: AppData,
    splitIndex: number,
    breadCrumbs: boolean,
    pageLink: PageType,
    organization?: OrganizationData,
    previewMode?: boolean,
}

export function DynamicPage(props: CustomPageProps) {
    const { app, splitIndex, breadCrumbs, pageLink, organization, previewMode } = props
    const page = usePage()
    // const page = app ? app.pages.find((x) => x.link === pageLink) : null
    const [scrollElem, setScrollElem] = useState()
    const standaloneOrg = useAppSelector((state: RootState) => state.app.standaloneOrg)
    const currentOrganization = useAppSelector((state: RootState) => state.app.organization)
    const showOptionbar = currentOrganization && currentOrganization.link == 'homegyde'
    const dispatch = useAppDispatch()
    const [finalLink, setFinalLink] = useState(pageLink)
    const [loading, setLoading] = useState(1)
    const [scrollInit, setScrollInit] = useState(null)

    // Admin permissions check
    const adminPermissions = useAdminPermissions()
    const hasEditPermissions = adminPermissions(app, organization || currentOrganization, [UserRole.ProjectEdit, UserRole.PagesEdit])
    const hasAdminPermissions = adminPermissions(app, organization || currentOrganization, [UserRole.OrganizationsEdit])


    // Extensive debugging for preview mode
    useEffect(() => {
        // Only show minimal debug info for errors
    }, [app, organization, pageLink, previewMode])

    useEffect(() => {
        setFinalLink(pageLink)
    }, [pageLink])

    useEffect(() => {
        setLoading(1)
        if (scrollInit == null) {
            return
        }
        // Give content a moment to layout and scroll to cached location
        setTimeout(() => {
            // Check for page anchor
            if (scrollElem && scrollInit != 0) {
                scrollElem.scrollTop = scrollInit
                setTimeout(() => {
                    setLoading(0)
                }, 100)
            } else {
                setLoading(0)
            }
        }, 100)
    }, [pageLink, scrollInit])

    useEffect(() => {
        if (!scrollElem) {
            return
        }

        function checkHash() {
            if (getWindowLocation().hash.length > 1) {
                const id = getWindowLocation().hash.slice(1, getWindowLocation().hash.length)

                switch (id) {
                    case 'register': {
                        // Check for scroll elem as page loads
                        const tryScroll = (tries = 5) => {
                            if (tries == 0) {
                                return
                            }
                            const targetElem = document.getElementById(id)
                            if (targetElem) {
                                targetElem.scrollIntoView({ behavior: 'smooth' })
                            } else {
                                setTimeout(() => {
                                    tryScroll(tries - 1)
                                }, 100)
                            }
                        }
                        setTimeout(() => {
                            tryScroll()
                        }, 1000)
                        break
                    }
                    case 'thankyou':
                        setFinalLink(id)
                        break
                }
            }
        }
        window.addEventListener('hashchange', checkHash)
        checkHash()
        return () => {
            window.removeEventListener('hashchange', checkHash)

        }
    }, [scrollElem])

    function handleScrollInitialize(x) {
        setScrollInit(x)
    }

    function handleRedirect(x) {
        if (scrollElem) {
            // Scroll to top
            setTimeout(() => {
                scrollElem.scrollTo({ top: 0, behavior: 'smooth' })
            }, 100)
            // scrollElem.scrollTop = 0
        }
        if (x.includes('#')) {
            const tokens = x.split('#')
            const hash = tokens[tokens.length - 1]
            window.location.hash = hash
        } else {
            dispatch(navigateAsync({ app, pageType: x }))
        }
    }

    function handleCloneTemplate() {
        
        if (!page?.template || !page?.id) {
            return
        }
        
        // Use InlineContentEditor's clone functionality instead of Redux and navigation
        // Trigger the content action to open clone mode in the editor
        window.dispatchEvent(new CustomEvent(CustomEventType.ContentAction, {
            detail: {
                action: 'copy',
                data: page,
                page: page
            }
        }))
    }

    function getCustomView() {
        let ContentComponent = null
        if (app) {
            ContentComponent = projectContent(app)
        } else if (organization) {
            ContentComponent = organizationContent(organization)
        }
        if (ContentComponent) {
            let finalPage = page
            
            // In preview mode, always use the page from the organization data to ensure correct page ID
            if (previewMode && organization && pageLink) {
                finalPage = organization.pages?.find((x) => x.link === pageLink)
                if (previewMode && finalPage) {
                    logger.info('DynamicPage: Using organization page for preview:', {
                        pageId: finalPage.id,
                        pageName: finalPage.name,
                        pageLink: finalPage.link,
                        originalPageId: page?.id,
                        originalPageName: page?.name
                    })
                }
            } else if (finalLink != page?.link) {
                if(app) {
                    finalPage = app?.pages.find((x) => x.link == finalLink)
                } else if (organization) {
                    finalPage = organization?.pages.find((x) => x.link == finalLink)
                }
            }
            
            return <Suspense fallback={<Spinner overlay />}>
                <ContentComponent 
                    app={app} 
                    organization={organization} 
                    link={finalLink} 
                    page={finalPage} 
                    onRedirect={handleRedirect} 
                    scrollElem={scrollElem} 
                    previewMode={props.previewMode}
                />
            </Suspense>
        }
        return <h3> Custom </h3>
    }
    let key = finalLink
    let style = {}
    if(page?.backgroundColor) {
        style.backgroundColor = `#${page.backgroundColor}`
        key = `${key}${page.backgroundColor}`
    }
    if (page?.draft && !hasEditPermissions) {
        return <Message info={ErrorMessage.MaintenanceModePage}/>
    }
    return <React.Fragment>
        {showOptionbar && <OptionBar app={app} splitIndex={splitIndex} titleText={app?.meta.name} />}
        <div className={`content dynamic scrollable ${loading ? ' loading' : ''}`} ref={setScrollElem} key={key} style={style}>
            {breadCrumbs && <BreadCrumbs app={app} splitIndex={splitIndex} />}
            {getCustomView()}
            {page?.pageType != PageType.ScreenSaver && !props.previewMode && <Footer />}
        </div>
        <ScrollHelper scrollElem={scrollElem} pageLink={finalLink} onScrollInitialize={handleScrollInitialize} />
    </React.Fragment>
}